import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuction } from '../../context/AuctionContext';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Button,
    Autocomplete,
} from '@mui/material';
import { collection, getDocs, doc, setDoc, getDoc } from "firebase/firestore";
import { httpsCallable } from 'firebase/functions';
import { db, functions } from "../../firebase";
import { sortPlayers } from '../../utils/sorting';
import { seasonPlayersCollection, seasonClubsCollection } from '../../services/players';
import { isActiveSeasonPlayer } from '../../constants/game';

const POSITION_MAP = {
    'Goalkeeper': 'Keeper',
    'Defender': 'Verdediger',
    'Midfielder': 'Middenvelder',
    'Attacker': 'Aanvaller',
    'Coach': 'Coach'
};

function AdminVeilinglijst() {
    const { auctionId } = useAuction();
    const [auction, setAuction] = useState(null);
    const [allPlayers, setAllPlayers] = useState([]);
    const [playerStats, setPlayerStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [clubFilter, setClubFilter] = useState('');
    const [positionFilter, setPositionFilter] = useState('');
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 50;
    const [auctionPlayerIds, setAuctionPlayerIds] = useState(new Set());
    const [seasonClubIds, setSeasonClubIds] = useState(new Set());

    const fetchAuctionPlayers = useCallback(async () => {
        if (!auctionId) return;
        try {
            const auctionPlayersRef = collection(db, 'auctions', auctionId, 'auctionPlayers');
            const querySnapshot = await getDocs(auctionPlayersRef);
            const playerIds = new Set(querySnapshot.docs.map(doc => doc.id));
            setAuctionPlayerIds(playerIds);
        } catch (err) {
            console.error("Error fetching auction players: ", err);
            setError("Kon de lijst met veiling-spelers niet laden.");
        }
    }, [auctionId]);

    useEffect(() => {
        if (!auctionId) return;
        setLoading(true);
        const auctionRef = doc(db, 'auctions', auctionId);
        getDoc(auctionRef).then(docSnap => {
            if (docSnap.exists()) {
                setAuction(docSnap.data());
            } else {
                setError('Veiling niet gevonden.');
            }
        }).catch(err => {
            console.error("Error fetching auction data: ", err);
            setError("Fout bij het ophalen van de veiling.");
        });
        fetchAuctionPlayers();
    }, [auctionId, fetchAuctionPlayers]);

    useEffect(() => {
        if (!auction?.season) return;

        const fetchPlayersForSeason = async () => {
            setLoading(true);
            try {
                const [playersSnap, clubsSnap] = await Promise.all([
                    getDocs(seasonPlayersCollection(auction.season)),
                    getDocs(seasonClubsCollection(auction.season)),
                ]);
                const fetchedPlayers = playersSnap.docs.map(doc => doc.data());
                const sortedPlayers = sortPlayers(fetchedPlayers);
                setAllPlayers(sortedPlayers);
                setSeasonClubIds(new Set(clubsSnap.docs.map(doc => Number(doc.data().id))));
            } catch (err) {
                console.error("Error fetching players for season:", err);
                setError("Kon de spelers voor dit seizoen niet laden.");
            } finally {
                setLoading(false);
            }
        };
        fetchPlayersForSeason();
    }, [auction]);

    useEffect(() => {
        if (allPlayers.length === 0) return;

        const fetchPlayerStats = async () => {
            try {
                const playerIds = allPlayers.map(p => String(p.id));
                const getPlayerStats = httpsCallable(functions, 'getPlayerStats');
                const result = await getPlayerStats({ playerIds, seasonId: auction.season });
                setPlayerStats(result.data);
            } catch (err) {
                console.error("Error fetching player stats:", err);
                // Non-critical error, so we don't block the UI
            }
        };

        fetchPlayerStats();
    }, [allPlayers, auction?.season]);

    const filteredPlayers = useMemo(() => {
        // Alleen spelers van een deelnemende seizoensclub. Verbergt zowel op Non-CL
        // gezette spelers als spelers die naar een andere niet-deelnemende club zijn
        // vertrokken.
        let players = allPlayers.filter(p => isActiveSeasonPlayer(p, seasonClubIds));
        if (searchTerm) {
            players = players.filter(p =>
                p.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        if (clubFilter) {
            players = players.filter(p => p.clubName === clubFilter);
        }
        if (positionFilter) {
            players = players.filter(p => p.position === positionFilter);
        }
        return players;
    }, [allPlayers, seasonClubIds, searchTerm, clubFilter, positionFilter]);

    const visiblePlayers = useMemo(() => {
        return filteredPlayers.slice(0, page * PAGE_SIZE);
    }, [filteredPlayers, page]);

    const observer = useRef();
    const lastPlayerElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && visiblePlayers.length < filteredPlayers.length) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, filteredPlayers.length, visiblePlayers.length]);

    const clubs = useMemo(() => {
        const clubNames = [...new Set(allPlayers.filter(p => isActiveSeasonPlayer(p, seasonClubIds)).map(p => p.clubName))];
        return clubNames.sort();
    }, [allPlayers, seasonClubIds]);

    const positions = useMemo(() => {
        const positionNames = [...new Set(allPlayers.map(p => p.position))];
        return positionNames.sort();
    }, [allPlayers]);

    const translatedPositions = useMemo(() => {
        return positions.map(p => ({ key: p, value: POSITION_MAP[p] || p })).sort((a, b) => a.value.localeCompare(b.value));
    }, [positions]);

    useEffect(() => {
        setPage(1);
    }, [searchTerm, clubFilter, positionFilter]);

    const handleAddPlayerToAuction = async (player) => {
        if (!auctionId) {
            setError("Geen veiling geselecteerd.");
            return;
        }
        try {
            const newAuctionOrder = auctionPlayerIds.size;
            const playerDocRef = doc(db, 'auctions', auctionId, 'auctionPlayers', String(player.id));
            const playerWithOrder = { ...player, auctionOrder: newAuctionOrder, auctioned: 0 };

            await setDoc(playerDocRef, playerWithOrder);
            setAuctionPlayerIds(prevIds => new Set(prevIds).add(String(player.id)));
        } catch (err) {
            console.error("Error adding player to auction: ", err);
            setError("Fout bij het toevoegen van speler aan de veiling.");
        }
    };

    if (loading && !auction) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Veilinglijst</Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
                Kies welke spelers geveild worden in <strong>{auction?.name}</strong>. De spelersgegevens
                zelf — statistieken, foto&apos;s, selecties — beheert de administrator centraal.
            </Typography>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={5}>
                        <TextField fullWidth label="Zoek op naam" variant="outlined" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                            sx={{ minWidth: 200 }}
                            options={clubs}
                            value={clubFilter || null}
                            onChange={(event, newValue) => {
                                setClubFilter(newValue || '');
                            }}
                            renderInput={(params) => <TextField {...params} label="Club" variant="outlined" />}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth variant="outlined" sx={{ minWidth: 200 }}>
                            <InputLabel>Positie</InputLabel>
                            <Select value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)} label="Positie">
                                <MenuItem value=""><em>Alle posities</em></MenuItem>
                                {translatedPositions.map(pos => (<MenuItem key={pos.key} value={pos.key}>{pos.value}</MenuItem>))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            <TableContainer component={Paper}>
                <Table stickyHeader aria-label="spelers tabel">
                    <TableHead>
                        <TableRow>
                            <TableCell>Speler</TableCell>
                            <TableCell>Positie</TableCell>
                            <TableCell>Club</TableCell>
                            <TableCell align="right">Wedstrijden</TableCell>
                            <TableCell align="right">Goals</TableCell>
                            <TableCell align="right">Assists</TableCell>
                            <TableCell align="right">Acties</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : visiblePlayers.map((player, index) => {
                            const stats = playerStats[player.id];
                            return (
                                <TableRow
                                    key={player.id}
                                    ref={visiblePlayers.length === index + 1 ? lastPlayerElementRef : null}
                                    hover
                                    tabIndex={-1}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar src={player.photoUrl} alt={player.name} />
                                            {player.name}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{POSITION_MAP[player.position] || player.position}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box component="img" src={player.clubLogo} alt={`${player.clubName} logo`} sx={{ height: 24, width: 24, objectFit: 'contain' }} />
                                            {player.clubName}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">{stats?.matches || 0}</TableCell>
                                    <TableCell align="right">{stats?.goals || 0}</TableCell>
                                    <TableCell align="right">{stats?.assists || 0}</TableCell>
                                    <TableCell align="right">
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); handleAddPlayerToAuction(player); }}
                                            disabled={auctionPlayerIds.has(String(player.id))}
                                        >
                                            {auctionPlayerIds.has(String(player.id)) ? 'Toegevoegd' : 'Toevoegen'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {visiblePlayers.length > 0 && visiblePlayers.length < filteredPlayers.length && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            )}

            {filteredPlayers.length === 0 && !loading && (
                <Typography sx={{ textAlign: 'center', p: 3 }}>Geen spelers gevonden die aan de criteria voldoen.</Typography>
            )}
        </Box>
    );
}

export default AdminVeilinglijst;